// Sends a round's scores to the API whenever there's signal. Each sync sends
// the whole card (at most 36 holes); the API merges per hole by recordedAt, so
// resending is harmless and a late, stale sync can't overwrite newer scores.

import type { RoundSync, Scorecard } from "@union/contracts";
import { ApiRequestError, ApiUnreachableError } from "@union/web-kit";
import { applyServerCard, getRound, type LocalRound, pendingHoles } from "./store.ts";

export type SyncState = "synced" | "pending" | "offline" | "error";

export async function syncRound(
  roundId: string,
  accessToken: string | undefined,
): Promise<{ round?: LocalRound; state: SyncState }> {
  const round = await getRound(roundId);
  if (!round) return { state: "error" };
  if (pendingHoles(round).length === 0) return { round, state: "synced" };

  if (!accessToken) return { round, state: "pending" };

  const body: RoundSync = {
    clubSlug: round.clubSlug,
    courseId: round.course.id,
    startedAt: round.startedAt,
    scores: Object.entries(round.scores).map(([hole, s]) => ({ hole: Number(hole), ...s })),
  };
  try {
    const res = await fetch(new URL(`/member/rounds/${roundId}`, import.meta.env.VITE_API_URL), {
      method: "PUT",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).catch((cause: unknown) => {
      throw new ApiUnreachableError(cause);
    });
    if (!res.ok) throw new ApiRequestError(res.status, "sync_failed", "");
    const updated = await applyServerCard((await res.json()) as Scorecard);
    const state = updated && pendingHoles(updated).length === 0 ? "synced" : "pending";
    return { round: updated ?? round, state };
  } catch (error) {
    return { round, state: error instanceof ApiUnreachableError ? "offline" : "error" };
  }
}
